import { useState } from 'react'
import { Box, IconButton, Tooltip } from '@mui/material'
import LayersIcon from '@mui/icons-material/Layers'
import AddIcon from '@mui/icons-material/Add'
import { ICON_XS } from '../common/theme'
import TaElementsModal from '../tabs/modals/TaElementsModal'
import { useAppActions } from '../AppContext'
import type { Question } from '../types'

export type AddElementsMode = 'structure' | 'answer' | 'scoring'

interface TaAddElementsButtonProps {
  // Which target field on the question is being edited:
  //   • 'structure' → question.structuretweaks
  //   • 'answer'    → question.answertweaks
  //   • 'scoring'   → question.bideval.scoringelements
  mode: AddElementsMode
  question: Question
  disabled?: boolean
  // Override the default tooltip text per call site.
  tooltip?: string
  testId?: string
}

// Self-contained "Add …" trigger that opens TaElementsModal in the matching
// mode and writes the user's picks back onto the right question field via
// updateQuestion. Use this anywhere we'd otherwise wire an IconButton + a
// separate modal mount: BidQuestionAnswerTab, BidQuestionStoryboardTab,
// TaScoringElements.
export default function TaAddElementsButton({
  mode, question, disabled, tooltip, testId,
}: TaAddElementsButtonProps) {
  const { updateQuestion } = useAppActions()
  const [open, setOpen] = useState(false)

  const defaultTooltips: Record<AddElementsMode, string> = {
    structure: 'Add Storyboard Tweaks',
    answer: 'Add Answer Tweaks',
    scoring: 'Add Scoring Elements',
  }
  const title = tooltip ?? defaultTooltips[mode]

  // Pick the right slice of the question to pre-seed the modal with, and
  // the right write-back patch when the user clicks Apply.
  const selected =
    mode === 'structure' ? (question.structuretweaks ?? []) :
    mode === 'answer' ? ((question as any).answertweaks ?? []) :
    ((question.bideval as any)?.scoringelements ?? [])

  const handleApply = (elements: any[]) => {
    if (mode === 'structure') {
      updateQuestion((question as any).questionsha, { structuretweaks: elements })
    } else if (mode === 'answer') {
      updateQuestion((question as any).questionsha, { answertweaks: elements } as any)
    } else {
      const prevBideval = (question.bideval as any) ?? {}
      updateQuestion((question as any).questionsha, {
        bideval: { ...prevBideval, scoringelements: elements },
      })
    }
  }

  return (
    <>
      <Tooltip title={title}>
        <span>
          <IconButton
            data-testid={testId}
            size="small"
            sx={{ p: 0.25, borderRadius: 1 }}
            disabled={disabled}
            onClick={() => setOpen(true)}
          >
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '1px' }}>
              <LayersIcon sx={{ fontSize: ICON_XS }} />
              <AddIcon sx={{ fontSize: `calc(${ICON_XS}px * 0.7)`, ml: '-2px' }} />
            </Box>
          </IconButton>
        </span>
      </Tooltip>
      <TaElementsModal
        open={open}
        onClose={() => setOpen(false)}
        mode={mode}
        question={question}
        selected={selected}
        onApply={handleApply}
      />
    </>
  )
}
